import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  LuFileText, LuDownload, LuCircleCheck, LuMail, LuBookOpen,
  LuLayers, LuScanLine, LuShieldCheck, LuBrainCircuit,
  LuSettings2, LuArrowRight, LuStar, LuZap, LuGlobe,
  LuLock, LuRadio, LuLoaderCircle,
} from 'react-icons/lu';
import { generateBrochurePDF } from '../utils/generateBrochurePDF';

const IMG = {
  hero:     'https://images.unsplash.com/photo-1587143718284-ffadf87e2d09?w=1200&q=80&auto=format',
  preview1: 'https://i.pinimg.com/originals/e3/26/0e/e3260e9059cca1928c373705d9c4cea8.jpg',
  preview2: 'https://www.airport-technology.com/wp-content/uploads/sites/14/2020/02/ansir-merge-conveyors-product-scaled.jpg',
  preview3: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80&auto=format',
  cover:    'https://images.unsplash.com/photo-1551269901-5c5e14c25df7?w=500&q=80&auto=format',
};

const CHAPTERS = [
  {
    num: '01',
    icon: <LuLayers size={20} />,
    title: 'System Architecture Overview',
    pages: '12 pages',
    desc: 'Complete diagrams of how check-in, sorting, and claim carousel systems connect. Includes scale layouts of major airport baggage halls.',
    color: 'var(--primary)',
    bg: 'var(--primary-pale)',
  },
  {
    num: '02',
    icon: <LuScanLine size={20} />,
    title: 'RFID & Barcode Technology',
    pages: '10 pages',
    desc: 'Technical deep-dive into tag types, read rates, reader placement, and the IATA Resolution 753 compliance requirements explained plainly.',
    color: 'var(--accent)',
    bg: 'var(--accent-pale)',
  },
  {
    num: '03',
    icon: <LuBrainCircuit size={20} />,
    title: 'AI Sorting & Automation',
    pages: '14 pages',
    desc: 'How machine learning algorithms route bags in real time, crossbelt sorter mechanics, and the data flow from BMS to individual diverters.',
    color: 'var(--accent-2)',
    bg: 'var(--accent-2-pale)',
  },
  {
    num: '04',
    icon: <LuShieldCheck size={20} />,
    title: 'Safety Systems & Emergency Response',
    pages: '8 pages',
    desc: 'Full coverage of E-stop networks, fire suppression integration, redundant belt routing, and the six-step emergency protocol used at hub airports.',
    color: 'var(--accent-3)',
    bg: 'var(--accent-3-pale)',
  },
  {
    num: '05',
    icon: <LuSettings2 size={20} />,
    title: 'Maintenance & Predictive Analytics',
    pages: '9 pages',
    desc: 'Maintenance schedules, IoT sensor data pipelines, predictive failure models, and real-world uptime improvement case studies from top-tier airports.',
    color: 'var(--primary)',
    bg: 'var(--primary-pale)',
  },
  {
    num: '06',
    icon: <LuGlobe size={20} />,
    title: 'Future Trends & Innovations',
    pages: '7 pages',
    desc: 'What comes next — autonomous baggage robots, biometric-linked routing, sustainable belt materials, and the roadmap to Level 5 automation.',
    color: 'var(--accent)',
    bg: 'var(--accent-pale)',
  },
];

const FEATURES = [
  { icon: <LuFileText size={18} />, label: '60 Pages', desc: 'Comprehensive depth' },
  { icon: <LuZap size={18} />,      label: 'Instant PDF', desc: 'Download immediately' },
  { icon: <LuRadio size={18} />,    label: 'HD Diagrams', desc: '40+ original visuals' },
  { icon: <LuLock size={18} />,     label: 'No Paywall', desc: 'Completely free' },
];

const PREVIEWS = [
  { img: IMG.preview1, caption: 'Full Airport System Architecture Diagram' },
  { img: IMG.preview2, caption: 'Technology & Automation Flow Charts' },
  { img: IMG.preview3, caption: 'Emergency Response Protocol Breakdown' },
];

const REVIEWS = [
  {
    name: 'Prof. Thomas Reid',
    role: 'Aerospace Engineering, MIT',
    quote: "One of the most thorough free resources I've seen on airport infrastructure. I recommend it to every graduate student.",
    stars: 5,
  },
  {
    name: 'Ananya Sharma',
    role: 'Ground Handling Manager, Bangalore Airport',
    quote: 'The maintenance chapter alone is worth downloading. It validated several procedures we already use and introduced two new ones.',
    stars: 5,
  },
  {
    name: 'Carlos Mendez',
    role: 'Airport Technology Journalist',
    quote: 'Exceptional clarity on RFID vs barcode. The diagrams make it immediately usable for non-technical stakeholders.',
    stars: 5,
  },
];

export default function Brochure() {
  const [form, setForm]       = useState({ name: '', email: '', org: '' });
  const [errors, setErrors]   = useState({});
  const [toast, setToast]     = useState({ show: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const timer                 = useRef(null);

  const showToast = (msg) => {
    clearTimeout(timer.current);
    setToast({ show: true, msg });
    timer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  /* ── validation ── */
  const validate = () => {
    const e = {};
    if (!form.name.trim())
      e.name = 'Please enter your name.';
    if (!form.email.trim())
      e.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Please enter a valid email address.';
    return e;
  };

  const handleDownload = (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setLoading(true);
    setTimeout(() => {
      try {
        generateBrochurePDF(form.name);
        showToast('Your brochure is downloading now!');
        setForm({ name: '', email: '', org: '' });
      } catch (err) {
        showToast('Download failed — please try again.');
      } finally {
        setLoading(false);
      }
    }, 80);
  };

  /* clear error on field change */
  const update = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(er => { const n = { ...er }; delete n[field]; return n; });
  };

  return (
    <div className="page-enter" style={{ paddingTop: 66 }}>

      {/* ══ HERO ══ */}
      <div className="br-hero">
        <div className="br-hero-bg" style={{ backgroundImage: `url(${IMG.hero})` }} />
        <div className="br-hero-overlay" />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="br-hero-inner">
            <div className="br-hero-left">
              <div className="section-tag" style={{ color: '#fff', background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.3)' }}>
                <LuFileText size={13} /> Free PDF Brochure
              </div>
              <h1 className="br-hero-title">
                The Complete Airport Baggage Systems Guide
              </h1>
              <p className="br-hero-sub">
                A 60-page technical PDF covering everything from belt architecture
                to AI sorting, RFID tracking, safety systems, and the future of airport automation.
              </p>
              <div className="br-hero-features">
                {FEATURES.map((f, i) => (
                  <div key={i} className="br-feature-chip">
                    <span style={{ color: 'rgba(255,255,255,.8)' }}>{f.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '.82rem', color: '#fff' }}>{f.label}</div>
                      <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.65)' }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Download form */}
            <div className="br-hero-form-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,var(--primary),var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <LuDownload size={18} />
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1rem', color: 'var(--text-2)' }}>Download Free PDF</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Instant — no account required</div>
                </div>
              </div>
              <form onSubmit={handleDownload} style={{ display: 'flex', flexDirection: 'column', gap: 12 }} noValidate>
                <div className="form-group">
                  <label htmlFor="br-name">Your Name</label>
                  <input
                    id="br-name" type="text" placeholder="Full name"
                    value={form.name} onChange={e => update('name', e.target.value)}
                    style={errors.name ? { borderColor: '#dc2626' } : {}}
                  />
                  {errors.name && <span className="br-field-error">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="br-email">Email Address</label>
                  <input
                    id="br-email" type="email" placeholder="your@email.com"
                    value={form.email} onChange={e => update('email', e.target.value)}
                    style={errors.email ? { borderColor: '#dc2626' } : {}}
                  />
                  {errors.email && <span className="br-field-error">{errors.email}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="br-org">Organisation <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span></label>
                  <input
                    id="br-org" type="text" placeholder="Airport, University, Company…"
                    value={form.org} onChange={e => update('org', e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                  {loading
                    ? <><LuLoaderCircle size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating PDF…</>
                    : <><LuDownload size={16} /> Download Brochure — Free</>
                  }
                </button>
                <div style={{ fontSize: '.74rem', color: 'var(--text-faint)', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <LuLock size={11} /> We never share your information
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ══ CHAPTER OVERVIEW ══ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">What's Inside</div>
            <h2 className="section-title">Six In-Depth Chapters</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              60 pages structured to take you from the fundamentals of belt architecture
              all the way to the cutting-edge future of airport baggage automation.
            </p>
          </div>
          <div className="br-chapters-grid">
            {CHAPTERS.map((ch, i) => (
              <div key={i} className={`br-chapter-card reveal delay-${(i % 4) + 1}`}>
                <div className="br-chapter-num-row">
                  <div className="br-chapter-num">{ch.num}</div>
                  <div className="br-chapter-icon" style={{ color: ch.color, background: ch.bg }}>{ch.icon}</div>
                  <div className="br-chapter-pages">{ch.pages}</div>
                </div>
                <div className="br-chapter-title">{ch.title}</div>
                <div className="br-chapter-desc">{ch.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ PREVIEW GALLERY ══ */}
      <section className="page-section">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">Inside The Brochure</div>
            <h2 className="section-title">Preview Select Pages</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              A glimpse at the visual quality and content depth you can expect in every chapter.
            </p>
          </div>
          <div className="br-preview-grid reveal">
            {PREVIEWS.map((p, i) => (
              <div key={i} className="br-preview-card">
                <div className="br-preview-img">
                  <img src={p.img} alt={p.caption} loading="lazy" />
                  <div className="br-preview-badge">
                    <LuBookOpen size={12} /> Preview
                  </div>
                </div>
                <div className="br-preview-caption">{p.caption}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ REVIEWS ══ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">Reader Reviews</div>
            <h2 className="section-title">What Professionals Say</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Trusted by engineers, academics, consultants, and aviation students worldwide.
            </p>
          </div>
          <div className="br-reviews-grid">
            {REVIEWS.map((r, i) => (
              <div key={i} className={`br-review-card reveal delay-${i + 1}`}>
                <div className="br-review-stars">
                  {Array.from({ length: r.stars }).map((_, j) => (
                    <LuStar key={j} size={14} style={{ fill: 'var(--warning)', color: 'var(--warning)' }} />
                  ))}
                </div>
                <div className="br-review-quote">"{r.quote}"</div>
                <div className="br-review-author">
                  <div style={{ fontWeight: 700, fontSize: '.87rem', color: 'var(--text-2)' }}>{r.name}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{r.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ BOTTOM CTA ══ */}
      <section className="page-section-sm">
        <div className="container">
          <div className="cta-banner reveal">
            <div className="cta-banner-bg-orb" />
            <LuDownload size={36} style={{ color: 'var(--primary)', marginBottom: 14 }} />
            <h2 className="section-title" style={{ marginBottom: 12 }}>Ready to Download?</h2>
            <p className="section-sub" style={{ margin: '0 auto 28px' }}>
              Get your free 60-page PDF brochure instantly. No account, no payment — just click and read.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="#download" className="btn btn-primary btn-lg" onClick={e => { e.preventDefault(); document.getElementById('br-email') && document.getElementById('br-email').focus(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <LuDownload size={18} /> Download Free Now
              </a>
              <Link to="/newsletter" className="btn btn-outline btn-lg">
                <LuMail size={18} /> Get the Newsletter <LuArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Toast */}
      <div className={`toast${toast.show ? '' : ' hidden'}`} role="status" aria-live="polite">
        <LuCircleCheck size={18} style={{ color: 'var(--accent-3)', flexShrink: 0 }} />
        {toast.msg}
      </div>
    </div>
  );
}
